import { useParams } from "react-router-dom";
import { useRouter } from "./router";
import { useAppDispatch, useAppSelector } from "./store";
import { ArrowRight, MessageSquare, Plus } from "lucide-react";
import { Layout } from "./chat/Chat";
import { clearChat } from "./slices/chatSlice";
import { useTranslation } from "react-i18next";

function capitalize(val: string) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

// Sub 1h -> 5 minutes ago
// Same day -> Today 12:34 PM
// Prior day -> Yesterday 12:34 PM
// This year  -> 05 Jan 12:34 PM
// Prior year -> 05 Jan 2024 12:34 PM
function formatDate(dateString: string, locale: string = "en-US") {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60)
  );
  const diffInDays = Math.floor(diffInMinutes / (60 * 24));

  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const timeFormat = new Intl.DateTimeFormat(locale, {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (diffInMinutes < 60) {
    return capitalize(rtf.format(-diffInMinutes, "minute"));
  } else if (diffInDays === 0) {
    const today = capitalize(rtf.format(0, "day"));
    return `${today} ${timeFormat.format(date)}`;
  } else if (diffInDays === 1) {
    const yesterday = capitalize(rtf.format(-1, "day"));
    return `${yesterday} ${timeFormat.format(date)}`;
  } else {
    const thisYear = now.getFullYear() === date.getFullYear();
    return new Intl.DateTimeFormat(locale, {
      month: "short",
      day: "numeric",
      year: thisYear ? undefined : "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  }
}

function truncateMessage(message: string, maxLength = 80) {
  if (message.length <= maxLength) return message;
  return message.slice(0, maxLength) + "...";
}

export function ListConversationsPage() {
  const {
    t,
    i18n: { language: currentLocale },
  } = useTranslation();
  const dispatch = useAppDispatch();
  const { frameId } = useParams();
  const { navigate } = useRouter();
  const conversations = useAppSelector((state) => state.conversations.data);

  const onNewConversation = () => {
    const func = async () => {
      await dispatch(clearChat());
      navigate(`/frames/${frameId}/chat`);
    };
    func();
  };

  return (
    <Layout secondaryAction="none">
      <h1 className="text-xl font-semibold px-6 py-4 border-b border-gray-200">
        {t("conversations.title")}
      </h1>

      {conversations.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-md mx-auto rounded-lg border-gray-200 p-6">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              {t("conversations.noConversationsYet")}
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {t("conversations.tapToStart")}
            </p>
          </div>
        </div>
      ) : (
        <ul className="flex-1 overflow-y-auto divide-y divide-gray-100">
          {conversations.map((conversation) => (
            <li
              key={conversation.conversation_id}
              className="group hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <button
                onClick={() =>
                  navigate(
                    `/frames/${frameId}/conversations/${conversation.conversation_id}`
                  )
                }
                className="w-full px-6 py-4 text-left flex flex-col gap-1 focus:outline-none focus:ring-2 ring-inset focus:ring-brand"
              >
                <div className="flex items-center gap-2 w-full">
                  <div className="flex-1">
                    <p className="text-sm text-gray-900 line-clamp-1">
                      {truncateMessage(
                        conversation.messages.find((m) => m.role == "user")
                          ?.content || "User Question"
                      )}
                    </p>
                    <span className="text-xs text-gray-500 whitespace-nowrap">
                      {formatDate(conversation.created_at, currentLocale)}
                    </span>
                  </div>
                  <ArrowRight
                    className="w-4 h-4 transition-transform duration-200 transform group-hover:translate-x-1"
                    viewBox="0 0 24 24"
                  />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="p-4 border-t border-gray-200">
        <button
          onClick={onNewConversation}
          className="w-full bg-brand text-white rounded-lg py-2 px-4 flex items-center justify-center gap-2 hover:bg-brand-hover transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>{t("conversations.startConversation")}</span>
        </button>
      </div>
    </Layout>
  );
}

export default ListConversationsPage;
